import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '@/services/api/users.api';
import { Spinner } from '@/components/atoms/Spinner';
import { Select } from '@/components/atoms/Select';
import { StudyTimeLineChart, type StudyTimeDailyPoint } from './StudyTimeLineChart';
import styles from './StudyTimeEvolutionPage.module.scss';

type StudyTimeRange = 'week' | 'month';

const RANGE_API: Record<StudyTimeRange, 'this_week' | 'this_month'> = {
  week: 'this_week',
  month: 'this_month',
};

export function StudyTimeEvolutionPage() {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<StudyTimeRange>('month');
  const [series, setSeries] = useState<StudyTimeDailyPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const periodOptions = useMemo(
    () => [
      { value: 'week' as const, label: t('studyTimeEvolution.periodWeek') },
      { value: 'month' as const, label: t('studyTimeEvolution.periodMonth') },
    ],
    [t]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    usersAPI
      .getStudyTimeDaily({ range: RANGE_API[range] })
      .then((res) => {
        if (!cancelled) setSeries(res.series);
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('studyTimeEvolution.loadError'));
          setSeries([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, range]);

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>{t('studyTimeEvolution.title')}</h1>
        <p className={styles.page__subtitle}>
          {range === 'week' && t('studyTimeEvolution.subtitleWeek')}
          {range === 'month' && t('studyTimeEvolution.subtitleMonth')}
        </p>
      </header>

      <div className={styles.page__chartWrap} aria-busy={loading}>
        <div className={styles.page__chartToolbar}>
          <Select
            fitContent
            placeholder={t('studyTimeEvolution.periodLabel')}
            value={range}
            onValueChange={(v) => setRange(v as StudyTimeRange)}
            options={periodOptions}
            className={styles.page__periodSelect}
          />
        </div>
        {loading ? (
          <div className={styles.page__state}>
            <Spinner size="lg" aria-label={t('common.loading')} />
          </div>
        ) : error ? (
          <p className={styles.page__error} role="alert">
            {error}
          </p>
        ) : (
          <StudyTimeLineChart data={series} locale={locale} />
        )}
      </div>
    </div>
  );
}

export default StudyTimeEvolutionPage;
